
import { FileText, Euro, Globe, Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/utils/format";

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  isLoading?: boolean;
}

const StatCard = ({ icon, label, value, isLoading = false }: StatCardProps) => (
  <div className="bg-white p-6 rounded-lg shadow-sm border">
    <div className="flex items-center gap-4">
      {icon}
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        {isLoading ? (
          <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
        ) : (
          <p className="text-2xl font-semibold">{value}</p>
        )}
      </div>
    </div>
  </div>
);

export const TenderStats = () => {
  const { t } = useTranslation();
  
  // Fetch saved tenders count and their total value
  const { data: savedTenderStats, isLoading } = useQuery({
    queryKey: ['saved-tender-stats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { count: 0, totalValue: 0 };

      // Get count of saved tenders
      const { data: tenders, error: countError } = await supabase
        .from('saved_tenders')
        .select('id, tender_data')
        .eq('user_id', user.id);

      if (countError) {
        console.error("Error fetching saved tenders:", countError);
        throw countError;
      }

      // Calculate total value from tender data
      let totalValue = 0;
      if (tenders && tenders.length > 0) {
        tenders.forEach(tender => {
          if (tender.tender_data) {
            // Look for value in various possible fields
            const tenderData = tender.tender_data;
            
            // Try to find value in different possible fields
            let value = null;
            
            // Type check to ensure we're working with an object
            if (typeof tenderData === 'object' && tenderData !== null && !Array.isArray(tenderData)) {
              if ('total_value' in tenderData && tenderData.total_value !== null) {
                value = parseFloat(String(tenderData.total_value));
              } else if ('estimated_value' in tenderData && tenderData.estimated_value !== null) {
                value = parseFloat(String(tenderData.estimated_value));
              } else if ('estimated-value-lot' in tenderData && 
                         Array.isArray(tenderData["estimated-value-lot"]) && 
                         tenderData["estimated-value-lot"].length > 0) {
                value = parseFloat(String(tenderData["estimated-value-lot"][0]));
              } else if ('match_reasons' in tenderData && 
                         typeof tenderData.match_reasons === 'object' && 
                         tenderData.match_reasons !== null && 
                         'estimated-value-lot' in tenderData.match_reasons && 
                         Array.isArray(tenderData.match_reasons["estimated-value-lot"]) && 
                         tenderData.match_reasons["estimated-value-lot"].length > 0) {
                value = parseFloat(String(tenderData.match_reasons["estimated-value-lot"][0]));
              }
            }
            
            if (!isNaN(value)) {
              totalValue += value;
            }
          }
        });
      }

      return {
        count: tenders?.length || 0,
        totalValue
      };
    }
  });
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
      <StatCard
        icon={<FileText className="h-8 w-8 text-primary" />}
        label={t('dashboard.savedTenders', 'Gemte udbud')}
        value={savedTenderStats?.count || 0}
        isLoading={isLoading}
      />
      <StatCard
        icon={<Euro className="h-8 w-8 text-primary" />}
        label={t('dashboard.totalValue', 'Værdi')}
        value={formatCurrency(savedTenderStats?.totalValue || 0)}
        isLoading={isLoading}
      />
    </div>
  );
};
